import { SBModel } from '@supplement-bacon/react-toolkit/models'

/**
 * Extension as per Instagram specifications
 *
 * https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/messaging-api#send-an-image-or-gif
 */

export interface AttachmentType extends SBModel {
  name: string
  extensions: Array<string>
  maxSize: number
  accept: Array<string>
}

export type FileType = 'pdf' | 'picture' | 'video' | 'audio'
export const attachmentTypes: Record<FileType, AttachmentType> = {
  pdf: {
    id: 3,
    name: 'PDF',
    extensions: ['pdf'],
    // 60 MB: large menus are accepted and compressed server-side (Ghostscript)
    // before storage. This matches the server upload limit — the dropzone blocks
    // anything bigger so users don't upload a file the server would reject.
    maxSize: 62914560,
    accept: ['application/pdf'],
  },
  picture: {
    id: 0,
    name: 'Picture',
    extensions: ['png', 'jpeg', 'gif', 'webp', 'jpg'],
    maxSize: 2050000, // 8Mb
    accept: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/jpg'],
  },
  video: {
    id: 1,
    name: 'Video',
    extensions: ['mp4', 'ogg', 'avi', 'mov', 'webm'],
    maxSize: 26214400, // 25Mb
    accept: [
      'video/mp4',
      'video/ogg',
      'video/x-msvideo',
      'video/quicktime',
      'video/webm',
    ],
  },
  audio: {
    id: 2,
    name: 'Audio',
    extensions: ['aac', 'm4a', 'waw', 'mp4'],
    maxSize: 26214400, // 25Mb
    accept: ['audio/aac', 'audio/mp4', 'audio/wav', 'audio/mp4'],
  },
}

/**
 * Describes the image an uploader expects. All fields optional so call sites
 * pass only what the design defines. `aspect` is width/height (e.g. 16/9); when
 * omitted but width + height are given, it is derived from them.
 */
export interface ExpectedImage {
  width?: number
  height?: number
  aspect?: number
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

/** Greatest common divisor, used to render a ratio in lowest terms. */
const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b))

/** Resolve the crop aspect ratio (width/height) from an ExpectedImage. */
export const resolveAspect = (exp: ExpectedImage): number | undefined => {
  if (exp.aspect && exp.aspect > 0) return exp.aspect
  if (exp.width && exp.height) return exp.width / exp.height
  return undefined
}

/**
 * Build the auto help line shown in the Dragger, e.g.
 * "Recommended format: 1200 × 675 px (16:9)". Returns undefined when there is
 * nothing meaningful to show.
 */
export const formatExpectedImage = (exp: ExpectedImage): string | undefined => {
  const parts: string[] = []

  if (exp.width && exp.height) {
    parts.push(`${exp.width} × ${exp.height} px`)
    const divisor = gcd(Math.round(exp.width), Math.round(exp.height))
    if (divisor > 0) {
      parts.push(`(${Math.round(exp.width) / divisor}:${Math.round(exp.height) / divisor})`)
    }
  } else {
    const aspect = resolveAspect(exp)
    if (aspect) {
      const w = Math.round(aspect * 1000)
      const divisor = gcd(w, 1000)
      parts.push(`(${w / divisor}:${1000 / divisor})`)
    }
  }

  if (parts.length === 0) return undefined
  return `Recommended format: ${parts.join(' ')}`
}

export const formatBytes = (bytes: number): string => {
  const units = ['octets', 'Ko', 'Mo', 'Go', 'To']
  let index = 0

  while (bytes >= 1024 && index < units.length - 1) {
    bytes /= 1024
    index++
  }

  return `${bytes.toFixed(0)} ${units[index]}`
}
